import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft } from "lucide-react";
import { useT } from "../lib/i18n";
import LanguageToggle from "../components/LanguageToggle";
import EventPoster from "../components/EventPoster";

function Poster({ url, title, sub, footer, testId }) {
    return (
        <div
            className="poster relative w-full aspect-[1/1.414] rounded-md overflow-hidden border border-gold/30 bg-[#050A08] p-10 flex flex-col items-center justify-between"
            data-testid={testId}
        >
            <div className="absolute inset-0 pointer-events-none noise" />
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(60%_40%_at_50%_0%,rgba(212,175,55,0.10),transparent_70%)]" />

            <div className="relative text-center">
                <div className="label-eyebrow !text-gold">John Scotts · Kungsträdgården</div>
                <div className="font-serif font-medium text-5xl mt-3">
                    Scotts<span className="gold-text">Club</span>
                </div>
            </div>

            <div className="relative bg-white p-6 rounded-md">
                <QRCodeSVG value={url} size={300} level="H" bgColor="#ffffff" fgColor="#050A08" includeMargin={false} />
            </div>

            <div className="relative text-center">
                <div className="font-serif text-3xl text-ink-primary">{title}</div>
                <div className="text-ink-secondary mt-2 text-sm tracking-[0.18em] uppercase">{sub}</div>
                <div className="gold-divider opacity-50 my-5 w-24 mx-auto" />
                <div className="text-ink-muted text-xs tracking-[0.22em] uppercase">{footer}</div>
            </div>
        </div>
    );
}

export default function Posters() {
    const { t } = useT();
    const navigate = useNavigate();
    const origin = typeof window !== "undefined" ? window.location.origin : "";

    const guestUrl = useMemo(() => `${origin}/`, [origin]);
    const staffUrl = useMemo(() => `${origin}/admin/login`, [origin]);

    return (
        <div className="min-h-[100dvh] text-ink-primary" data-testid="posters-page">
            {/* print css */}
            <style>{`
                @media print {
                    @page { size: A4 portrait; margin: 12mm; }
                    body { background: #fff !important; }
                    .no-print { display: none !important; }
                    .poster {
                        break-inside: avoid;
                        page-break-after: always;
                        border: none !important;
                        box-shadow: none !important;
                        background: #050A08 !important;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                        max-width: 100%;
                        margin: 0 auto;
                    }
                    .poster:last-child { page-break-after: auto; }
                    .posters-grid { display: block !important; }
                    .posters-grid > * { margin-bottom: 0 !important; }
                }
            `}</style>

            <header className="sticky top-0 z-30 glass-strong border-b border-emerald-line/60 no-print">
                <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 text-ink-secondary hover:text-gold text-xs tracking-[0.22em] uppercase"
                        data-testid="back"
                    >
                        <ArrowLeft size={14} /> Back
                    </button>
                    <div className="font-serif text-lg">{t("posters.title")}</div>
                    <div className="flex items-center gap-4">
                        <LanguageToggle />
                        <button
                            onClick={() => window.print()}
                            data-testid="print-button"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-black rounded-sm font-medium hover:bg-gold-bright text-sm"
                        >
                            <Printer size={14} /> {t("posters.print")}
                        </button>
                    </div>
                </div>
                <div className="gold-divider opacity-30" />
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10">
                <div className="no-print mb-10">
                    <div className="label-eyebrow">{t("posters.eyebrow")}</div>
                    <h1 className="font-serif font-medium text-3xl md:text-4xl mt-3">{t("posters.title")}</h1>
                    <p className="text-ink-secondary mt-3 max-w-2xl">{t("posters.subtitle")}</p>
                </div>

                <div className="posters-grid grid md:grid-cols-2 gap-10">
                    <Poster
                        testId="poster-guest"
                        url={guestUrl}
                        title={t("posters.guest_title")}
                        sub={t("posters.guest_sub")}
                        footer="Hamngatan 19 · Stockholm"
                    />
                    <Poster
                        testId="poster-staff"
                        url={staffUrl}
                        title={t("posters.staff_title")}
                        sub={t("posters.staff_sub")}
                        footer="Hamngatan 19 · Stockholm"
                    />
                </div>

                {/* Event poster — reads live from /public/next-event */}
                <div className="posters-grid mt-10">
                    <div className="no-print mb-6">
                        <div className="label-eyebrow">Event Flyer</div>
                        <h2 className="font-serif text-2xl md:text-3xl mt-2">Next Members Night — printable A4</h2>
                        <p className="text-ink-secondary mt-2 text-sm max-w-2xl">
                            Auto-updates from your event settings. Manage the date and title at{" "}
                            <Link to="/admin/event" className="text-gold hover:underline">/admin/event</Link>.
                        </p>
                    </div>
                    <div className="max-w-2xl mx-auto">
                        <EventPoster />
                    </div>
                </div>

                <div className="no-print mt-8 text-center">
                    <Link
                        to="/admin"
                        className="text-ink-secondary hover:text-gold text-xs tracking-[0.22em] uppercase"
                    >
                        ← Back to admin
                    </Link>
                </div>
            </main>
        </div>
    );
}
